import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import type {
  AnfitriaoDashboardAvaliacoes,
  AnfitriaoDashboardLinks,
  AnfitriaoDashboardReserva,
  AnfitriaoDashboardServico,
  AnfitriaoDashboardSummary,
} from "@/types/anfitriao-dashboard";

import ReservaDetalhesModal, { type ReservaDetalheModal } from "@/components/ReservaDetalhesModal";
import ReservaCollabAction from "@/components/ReservaCollabAction";

import {
  CalendarDays,
  ChevronRight,
  ClipboardList,
  MessageSquareText,
  PawPrint,
  ShieldCheck,
  Star,
  TrendingUp,
} from "lucide-react";

const statusColors: Record<string, string> = {
  confirmada: "bg-forest-light text-forest",
  pendente: "bg-muted text-muted-foreground",
  recusada: "bg-destructive/10 text-destructive",
  cancelada: "bg-muted text-muted-foreground",
  encaminhada: "bg-purple-100 text-purple-700",
  concluida: "bg-forest-light text-forest",
};

function formatMoney(value?: number | null) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatStatus(status?: string) {
  if (!status) return "—";
  return status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " ");
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

async function postJson(url: string) {
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-TOKEN": csrfToken(),
    },
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || data?.ok === false) {
    throw new Error(data?.message || "Não foi possível concluir a operação.");
  }

  return data;
}

function Stars({ value }: { value: number }) {
  const nota = Math.max(0, Math.min(5, Math.round(value || 0)));

  return (
    <div className="flex items-center gap-0.5" aria-label={`Nota ${value} de 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= nota ? "fill-[#d4af37] text-[#d4af37]" : "text-slate-300"}`}
          strokeWidth={1.6}
        />
      ))}
    </div>
  );
}

export function HeroBannerAnfitriao({
  firstName,
  links,
}: {
  firstName: string;
  links: AnfitriaoDashboardLinks;
}) {
  return (
    <div className="gradient-primary rounded-2xl p-6 md:p-8 flex items-center justify-between animate-fade-in-up">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold text-primary-foreground">
          Olá, {firstName}! 👋
        </h1>
        <p className="mt-1 text-primary-foreground/80 text-sm md:text-base">
          Gerencie seus serviços, reservas e disponibilidade em um só lugar.
        </p>

        <div className="flex gap-2 mt-4 flex-wrap">
          <Button asChild size="sm" variant="secondary" className="gap-1.5 font-semibold">
            <a href={links.agenda}>
              <CalendarDays className="h-3.5 w-3.5" />
              Disponibilidade
            </a>
          </Button>

          <Button
            asChild
            size="sm"
            variant="outline"
            className="gap-1.5 font-semibold border-border text-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <a href="/anfitriao/dashboard?aba=reservas">
              <ClipboardList className="h-3.5 w-3.5" />
              Reservas
            </a>
          </Button>
        </div>
      </div>

      <span className="hidden md:block text-6xl animate-floating">🐾</span>
    </div>
  );
}

export function SummarySection({ summary }: { summary: AnfitriaoDashboardSummary }) {
  const items = [
    { label: "Serviços ativos", value: summary.servicos_ativos },
    { label: "Pendentes", value: summary.reservas_pendentes },
    { label: "Confirmadas", value: summary.reservas_confirmadas },
    { label: "Tutores atendidos", value: summary.tutores_atendidos },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {items.map((item, index) => (
        <Card
          key={item.label}
          className={`card-shadow border-0 animate-fade-in-up stagger-${index + 1}`}
          style={{ opacity: 0 }}
        >
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">{item.label}</p>
            <p className="mt-2 text-3xl font-extrabold text-foreground">{item.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ServicosSection({
  servicos,
  onServicoClick,
}: {
  servicos: AnfitriaoDashboardServico[];
  onServicoClick?: (servico: AnfitriaoDashboardServico) => void;
}) {
  return (
    <section className="rounded-3xl bg-white/95 shadow-sm border border-black/5 p-4 md:p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Serviços Ativos</h2>
        <span className="text-sm text-gray-500">{servicos.length} serviço(s)</span>
      </div>

      {servicos.length === 0 ? (
        <div className="rounded-2xl bg-gray-50 p-4 text-sm text-gray-500">
          Nenhum serviço cadastrado até o momento.
        </div>
      ) : (
        <div className="space-y-4">
          {servicos.map((servico) => (
            <div
              key={servico.id}
              onClick={() => onServicoClick?.(servico)}
              className="rounded-2xl bg-gray-50 p-4 cursor-pointer transition hover:bg-gray-100"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-medium text-gray-900">{servico.nome}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {servico.tipo_precificacao || "Não informado"} • {formatMoney(servico.preco_base)}
                  </p>

                  {(servico.preco_banho || servico.preco_tosa || servico.preco_banho_tosa) && (
                    <p className="text-xs text-gray-500 mt-1">
                      Banho {formatMoney(servico.preco_banho || 0)} • Tosa {formatMoney(servico.preco_tosa || 0)} • Combo {formatMoney(servico.preco_banho_tosa || 0)}
                    </p>
                  )}

                  <p className="text-sm text-gray-500 mt-1">
                    {servico.disponibilidades_count ?? 0} disponibilidade(s)
                  </p>
                </div>

                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-200 text-gray-700">
                  {servico.status || "Ativo"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function AvaliacoesSectionAnfitriao({
  avaliacoes,
}: {
  avaliacoes?: AnfitriaoDashboardAvaliacoes;
}) {
  const dados = avaliacoes ?? {
    media: 0,
    total: 0,
    total_comentarios: 0,
    distribuicao: {},
    recebidas: [],
    feitas: [],
  };

  const recebidasComComentario = dados.recebidas.filter(
    (avaliacao) => avaliacao.tem_comentario || avaliacao.comentario
  );
  const total = Math.max(0, Number(dados.total || 0));
  const media = Number(dados.media || 0);
  const taxaComentarios = total > 0 ? Math.round((dados.total_comentarios / total) * 100) : 0;

  return (
    <div className="space-y-5">
      <section className="rounded-3xl border border-black/5 bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Reputação pública
            </p>
            <h2 className="mt-1 text-2xl font-extrabold text-foreground">
              O que tutores dizem sobre você
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
              Acompanhe sua nota, leia cada comentário recebido e mantenha o histórico das avaliações que você enviou aos tutores.
            </p>
          </div>

          <div className="rounded-3xl bg-forest-light px-6 py-5 text-center">
            <div className="flex items-center justify-center gap-2">
              <Star className="h-6 w-6 fill-[#d4af37] text-[#d4af37]" />
              <span className="text-4xl font-extrabold text-foreground">{media.toFixed(1)}</span>
            </div>
            <p className="mt-1 text-sm font-semibold text-forest">
              {total} avaliação{total === 1 ? "" : "es"} recebida{total === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <TrendingUp className="h-4 w-4 text-forest" /> Média geral
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <Stars value={media} />
              <span className="text-xl font-bold text-slate-900">{media.toFixed(1)}</span>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <MessageSquareText className="h-4 w-4 text-forest" /> Comentários
            </div>
            <p className="mt-3 text-xl font-bold text-slate-900">
              {dados.total_comentarios}
              <span className="ml-2 text-sm font-medium text-muted-foreground">({taxaComentarios}%)</span>
            </p>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <ShieldCheck className="h-4 w-4 text-forest" /> Confiança
            </div>
            <p className="mt-3 text-xl font-bold text-slate-900">{total > 0 ? "Ativa" : "Em construção"}</p>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-black/5 bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-foreground">Comentários recebidos</h3>
            <p className="text-sm text-muted-foreground">Feedbacks públicos deixados por tutores depois de reservas concluídas.</p>
          </div>
          <span className="text-sm font-semibold text-muted-foreground">
            {recebidasComComentario.length} comentário{recebidasComComentario.length === 1 ? "" : "s"}
          </span>
        </div>

        {recebidasComComentario.length > 0 ? (
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            {recebidasComComentario.map((avaliacao) => (
              <article key={avaliacao.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-forest-light text-forest text-sm font-bold">
                        {(avaliacao.pessoa_nome || "T").charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-foreground">{avaliacao.pessoa_nome}</p>
                      <p className="text-xs text-muted-foreground">
                        {avaliacao.servico_nome || "Serviço"} {avaliacao.data ? `• ${avaliacao.data}` : ""}
                      </p>
                    </div>
                  </div>
                  <Stars value={avaliacao.nota} />
                </div>
                <p className="mt-4 text-sm leading-6 text-slate-700">“{avaliacao.comentario}”</p>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-2xl bg-slate-50 p-6 text-center">
            <p className="text-sm font-semibold text-slate-700">Ainda não há comentários recebidos.</p>
            <p className="mt-1 text-sm text-muted-foreground">Quando tutores avaliarem suas reservas, os comentários aparecerão aqui.</p>
          </div>
        )}
      </section>

      <section className="rounded-3xl border border-black/5 bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-foreground">Avaliações que você fez</h3>
            <p className="text-sm text-muted-foreground">Histórico das avaliações enviadas por você aos tutores.</p>
          </div>
          <span className="text-sm font-semibold text-muted-foreground">
            {dados.feitas.length} enviada{dados.feitas.length === 1 ? "" : "s"}
          </span>
        </div>

        {dados.feitas.length > 0 ? (
          <div className="mt-5 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-100">
            {dados.feitas.map((avaliacao) => (
              <article key={avaliacao.id} className="bg-white p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="text-sm font-bold text-foreground">{avaliacao.pessoa_nome}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {avaliacao.servico_nome || "Serviço"} {avaliacao.data ? `• ${avaliacao.data}` : ""}
                    </p>
                  </div>
                  <Stars value={avaliacao.nota} />
                </div>
                {avaliacao.comentario ? (
                  <p className="mt-3 text-sm leading-6 text-slate-700">“{avaliacao.comentario}”</p>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">Sem comentário escrito.</p>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-2xl bg-slate-50 p-6 text-center">
            <p className="text-sm font-semibold text-slate-700">Você ainda não avaliou tutores.</p>
            <p className="mt-1 text-sm text-muted-foreground">Depois do checkout, suas avaliações enviadas aparecerão neste histórico.</p>
          </div>
        )}
      </section>
    </div>
  );
}

export function ReservasSectionAnfitriao({
  reservas,
  links,
}: {
  reservas: AnfitriaoDashboardReserva[];
  links: AnfitriaoDashboardLinks;
}) {
  const [reservasState, setReservasState] = useState(reservas);
  const [modalReservaOpen, setModalReservaOpen] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState<ReservaDetalheModal | null>(null);
  const [acaoReservaCarregando, setAcaoReservaCarregando] = useState<"aceitar" | "recusar" | "checkin" | "checkout" | null>(null);

  async function abrirDetalhesReserva(reserva: AnfitriaoDashboardReserva) {
    try {
      const resp = await fetch(`/reserva/${reserva.id}/detalhes-json`, {
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!resp.ok) {
        throw new Error("Falha ao buscar detalhes da reserva");
      }

      const dados = await resp.json();

      setReservaSelecionada({
        id: dados.id,
        tutor: dados.tutor || dados.tutor_nome || reserva.tutor_nome || "Tutor",
        tutorId: dados.tutor_id || "",
        telefoneTutor: dados.telefone_tutor || "",
        servico: dados.servico || reserva.servico_nome || "Serviço",
        modalidadeServico: dados.modalidade_servico || "",
        status: dados.status || reserva.status || "pendente",
        dataInicio: dados.data_inicio || reserva.data_inicio || "",
        dataFim: dados.data_fim || reserva.data_fim || "",
        horaInicio: dados.hora_inicio || reserva.hora_inicio || "",
        horaFim: dados.hora_fim || reserva.hora_fim || "",
        checkinRealizadoEm: dados.checkin_realizado_em || null,
        checkoutRealizadoEm: dados.checkout_realizado_em || null,
        mensagem: dados.mensagem || "",
        valorLiquido: dados.valor_liquido ?? dados.pagamento?.valor_liquido ?? null,
        adicionalCheckoutValor: dados.adicional_checkout_valor ?? null,
        adicionalCheckoutTipo: dados.adicional_checkout_tipo ?? null,
        adicionalCheckoutStatus: dados.adicional_checkout_status ?? null,
        checkoutAdicionalUrl: dados.checkout_adicional_url ?? null,
        checkoutAdicionalExternoUrl: dados.checkout_adicional_externo_url ?? null,
        checkoutAdicionalWhatsappUrl: dados.checkout_adicional_whatsapp_url ?? null,
        avaliacao: dados.avaliacao ?? null,
        pets: Array.isArray(dados.pets) ? dados.pets : [],
        chatUrl: dados.chat_url || links.mensagens || null,
      });

      setModalReservaOpen(true);
    } catch (error) {
      console.error(error);
      alert("Não foi possível carregar os detalhes da reserva.");
    }
  }

  function fecharDetalhesReserva() {
    setModalReservaOpen(false);
    setReservaSelecionada(null);
  }

  function atualizarReservaLocal(id: string, updates: Partial<ReservaDetalheModal>) {
    setReservaSelecionada((atual) => (atual && String(atual.id) === id ? { ...atual, ...updates } : atual));

    setReservasState((atuais) =>
      atuais.map((item) =>
        String(item.id) === id
          ? {
              ...item,
              status: typeof updates.status === "string" && updates.status ? updates.status : item.status,
            }
          : item
      )
    );
  }

  async function executarAcaoReserva(
    id: string,
    acao: "aceitar" | "recusar" | "checkin" | "checkout",
    endpoint: string,
    updates: Partial<ReservaDetalheModal>
  ) {
    if (acaoReservaCarregando) return;

    try {
      setAcaoReservaCarregando(acao);
      await postJson(endpoint);
      atualizarReservaLocal(id, updates);
    } catch (error) {
      console.error(error);
      alert(error instanceof Error ? error.message : "Não foi possível concluir a ação.");
    } finally {
      setAcaoReservaCarregando(null);
    }
  }

  async function aceitarReserva(id: string) {
    await executarAcaoReserva(id, "aceitar", `/anfitriao/reservas/${id}/aceitar`, { status: "confirmada" });
  }

  async function recusarReserva(id: string) {
    if (!window.confirm("Tem certeza que deseja recusar esta reserva?")) return;
    await executarAcaoReserva(id, "recusar", `/anfitriao/reservas/${id}/recusar`, { status: "recusada" });
  }

  async function realizarCheckin(id: string) {
    await executarAcaoReserva(id, "checkin", `/anfitriao/reservas/${id}/check-in`, {
      checkinRealizadoEm: new Date().toISOString(),
    });
  }

  async function realizarCheckout(id: string) {
    await executarAcaoReserva(id, "checkout", `/anfitriao/reservas/${id}/check-out`, {
      checkoutRealizadoEm: new Date().toISOString(),
      status: "concluida",
    });
  }

  return (
    <>
      <Card className="card-shadow border-0 animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-forest" />
            Próximas Reservas
          </CardTitle>

          <Button asChild variant="link" size="sm" className="text-xs font-semibold">
            <a href="/anfitriao/dashboard?aba=reservas">
              Ver todas <ChevronRight className="h-3.5 w-3.5" />
            </a>
          </Button>
        </CardHeader>

        <CardContent>
          {reservasState.length > 0 ? (
            <div className="space-y-3">
              {reservasState.map((r) => (
                <div key={r.id} className="flex items-start gap-3 rounded-xl bg-muted/50 p-3 transition-colors hover:bg-muted">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-forest-light text-forest text-sm font-bold">
                      {(r.tutor_nome || "T").charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-foreground">{r.tutor_nome || "Tutor"}</span>

                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${statusColors[r.status] || "bg-muted text-muted-foreground"}`}>
                        {formatStatus(r.status)}
                      </span>
                    </div>

                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {r.servico_nome || "Serviço"} • {r.data_inicio}
                      {r.hora_inicio ? ` • ${r.hora_inicio}` : ""}
                      {r.data_fim ? ` → ${r.data_fim}` : ""}
                      {r.hora_fim ? ` • ${r.hora_fim}` : ""}
                    </p>

                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" className="text-xs h-8" onClick={() => abrirDetalhesReserva(r)}>
                        Ver detalhes
                      </Button>

                      <Button asChild variant="ghost" size="sm" className="text-xs h-8">
                        <a href={links.mensagens}>Abrir chat</a>
                      </Button>

                      <ReservaCollabAction reserva={r} onSent={(reservaId) => atualizarReservaLocal(String(reservaId), { status: "encaminhada" })} />

                      {r.pode_avaliar && r.avaliacao_url ? (
                        <Button asChild size="sm" className="text-xs h-8">
                          <a href={r.avaliacao_url}>{r.label_avaliacao || "Avaliar"}</a>
                        </Button>
                      ) : r.ja_avaliou ? (
                        <Button variant="secondary" size="sm" className="text-xs h-8" disabled>
                          Avaliação enviada
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <PawPrint className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">Nenhuma reserva próxima.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <ReservaDetalhesModal
        open={modalReservaOpen}
        onClose={fecharDetalhesReserva}
        reserva={reservaSelecionada}
        viewerRole="anfitriao"
        onAceitar={aceitarReserva}
        onRecusar={recusarReserva}
        onRealizarCheckin={realizarCheckin}
        onRealizarCheckout={realizarCheckout}
        acaoReservaCarregando={acaoReservaCarregando}
      />
    </>
  );
}
